import { IResponse } from '../interface';
import { Logger } from './logger';
import {
    API_KEY,
    API_URL,
    APP_VERSION,
    IS_LOCAL,
    IS_PRODUCTION,
} from 'src/constants/env';
import { buildSig } from './sig';
import { currentSecords } from './time';
import { extend } from './common';
import { LSTORAGE_KEY, PLATFORM } from 'src/constants';
import CookieStore from './cookie';
import LocalStore from './storage';
// const FETCH_TIMEOUT = 10000;

export const buildParamsQuery = (object: any) => {
    let items = Object.keys(object).map(function (key) {
        if (
            object[key] !== 'undefined' &&
            object[key] !== 'null' &&
            object[key] !== undefined &&
            object[key] !== null &&
            object[key] !== ''
        ) {
            return `${key}=${encodeURIComponent(object[key])}`;
        } else return undefined;
    });
    let result = items.filter((t) => t !== undefined).join('&');
    return result;
};

export const buildURLWithParam = (url: string, params: object) => {
    if (!params) {
        return url;
    }
    const query = buildParamsQuery(params);

    return url + (!query ? '' : '?' + query);
};

export function buildParamsHasSig(path: string, params: any = {}) {
    params['ctime'] = currentSecords();
    params['version'] = APP_VERSION;
    params['sig'] = buildSig(path, params);
    params['apiKey'] = API_KEY;
    return params;
}

export function _fetchWithTimeout(
    url: string,
    params?: {},
    options = {},
    time: number = 500,
) {
    let isTimeout = false;
    return new Promise((resolve, reject) => {
        const timeout = setTimeout(function () {
            isTimeout = true;
            reject(new Error('TIMEOUT | url: ' + url));
        }, time);

        const exParams = extend({}, params);

        if (IS_LOCAL) {
            exParams.userId = '1644839165182046770';
        }

        _fetch(url, exParams, options)
            .then(function (response) {
                clearTimeout(timeout);
                if (!isTimeout) {
                    resolve(response);
                }
            })
            .catch(function (err) {
                if (isTimeout) return;
                reject(err);
            });
    });
}

export function _fetch(url: string, params?: {}, options?: {}) {
    const urlInst = new URL(url);
    if (urlInst.pathname && url?.indexOf(API_URL) > -1) {
        params = buildParamsHasSig(urlInst.pathname, params);
    }

    const _url = buildURLWithParam(url, params || {});

    return fetch(_url, { ...options })
        .then(toJson)
        .then((e) => validResp(_url, e));
}

export function fetchUrl(url: string, options?: {}) {
    const result = fetch(url, { ...options })
        .then(toJson)
        .then((e) => validResp(url, e));
    return result;
}

export const getWithPath = (
    path: string,
    params?: {},
    options?: {},
): Promise<IResponse<any>> => {
    const exOptions = extend({}, options, {
        credentials: 'include',
    });
    return getWithUrl(API_URL + path, params, exOptions);
};

export const getWithUrl = (
    url: string,
    params?: {},
    options?: {},
): Promise<IResponse<any>> => {
    const exParams = extend({}, params, {
        platform: url.indexOf('/get/config') > -1 ? PLATFORM.WEB : PLATFORM.WAP,
    });
    // const exParams = extend({}, params, {
    //     platform: PLATFORM.WAP,
    // });

    if (IS_LOCAL) {
        exParams.userId = '1644839165182046770';
    }

    if (LocalStore.get(LSTORAGE_KEY.BM_UID)) {
        exParams.userId = LocalStore.get(LSTORAGE_KEY.BM_UID);
    }
    return _fetch(url, exParams, options);
};

export const post = (
    url: string,
    params = {},
    data: any = {},
    options: any = {},
): Promise<IResponse<any>> => {
    const formData = options.body ? options.body : new URLSearchParams();
    for (const key in data) {
        if (data.hasOwnProperty(key) && data[key]) {
            formData.append(key, data[key]);
        }
    }
    const exParams = extend({}, params, {
        platform: PLATFORM.WAP,
    });

    if (IS_LOCAL) {
        exParams.userId = '1644839165182046770';
    }

    if (LocalStore.get(LSTORAGE_KEY.BM_UID)) {
        exParams.userId = LocalStore.get(LSTORAGE_KEY.BM_UID);
    }

    return _fetch(url, exParams, extend({ body: formData }, options));
};

export const postWithPath = (
    path: string,
    params?: {},
    data?: {},
    options?: {},
): Promise<IResponse<any>> => {
    const exOptions = extend({}, options, {
        credentials: 'include',
    });
    return postWithUrl(API_URL + path, params, data, exOptions);
};

export const postWithUrl = (
    url: string,
    params?: {},
    data?: {},
    options?: {},
): Promise<IResponse<any>> => {
    return post(url, params, data, extend({ method: 'POST' }, options));
};

function toJson(resp: any) {
    if (resp.ok) {
        return resp.json();
    }

    return Promise.reject(resp);
}

function validResp<T>(
    url: string,
    resp: IResponse<T>,
    forcePushLog: boolean = false,
): Promise<IResponse<T>> {
    if (!resp || resp.err < 0) {
        if (!IS_PRODUCTION || forcePushLog) {
            Logger.error(resp.err, url, resp.err, resp.msg);
        }

        return Promise.reject(resp);
    }

    return Promise.resolve(resp);
}

export function fetchRetry(
    _fetch: () => Promise<any>,
    n: number = 2,
    ms: number = 500,
): any {
    return new Promise((resolve, reject) => {
        return _fetch()
            .then((res) => resolve(res))
            .catch((error) => {
                if (error?.err) {
                    return reject(error);
                }
                if (n === 1) {
                    Logger.error('fetchRetry', error);
                    return reject(error);
                }

                return setTimeout(() => {
                    return fetchRetry(_fetch, n - 1, ms).then(resolve, reject);
                }, ms);
            });
    });
}

export function fetchWithTimeout(
    path: string,
    params: any = {},
    options = {},
    time: number = 200,
    credentials: boolean = true,
): any {
    let isTimeout = false;
    const _timeOut = params.timeout || time;
    Logger.debug(
        'fetchWithTimeout | path',
        path,
        ' | params:',
        params,
        ' | timeout:',
        _timeOut,
    );

    return new Promise((resolve, reject) => {
        const timeout = setTimeout(function () {
            isTimeout = true;
            Logger.errorWithType(
                'timeout',
                -1,
                `fetch ${
                    API_URL + path
                } timeout , cookies _live1_bm_contents_read: ${CookieStore.get(
                    '_live1_bm_contents_read',
                )}`,
            );
            reject(new Error('TIMEOUT | url: ' + path));
        }, _timeOut);

        params.platform = PLATFORM.WAP;
        const exOptions = credentials
            ? extend({}, options, {
                  credentials: 'include',
              })
            : options;

        _fetch(API_URL + path, params, exOptions)
            .then(function (response) {
                clearTimeout(timeout);
                if (!isTimeout) {
                    resolve(response);
                }
            })
            .catch(function (err) {
                if (isTimeout) return;
                reject(err);
            });
    });
}
